import Button from "@/components/Button";
import { useMutation } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import { UPDATE_PERSON_MUTATION } from "@/features/person/queries";
import TextField from "@/components/TextField";
import { EditProps } from "./types";
import Select from "@/components/Select";
import DateSelect from "@/components/DateSelect/DateSelect";
import { DateValueType } from "react-tailwindcss-datepicker";
import { genderOptions, toInput } from "../../../utils/utils";
import { digitsOnly, formatSSN, isValidSsn, ssnValidationMessage } from "../../../utils/ssn";
import { DateOfBirth } from "@/types";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";

type FieldValues = {
  informalName: string;
  preferredLastName: string;
  gender: string;
  dateOfBirth?: DateValueType | string;
  ssn?: string;
  spouseName?: string;
};

interface PersonalEditModalProps extends EditProps {
  informalName?: string;
  preferredLastName?: string;
  dob?: DateOfBirth;
  gender?: string;
  ssn?: string;
  spouseName?: string;
}

const padNumber = (n: number) => String(n).padStart(2, "0");

const formatForDateSelect = (dob?: DateOfBirth) => {
  if (!dob || !dob.year || !dob.month || !dob.day) return null;
  const date = `${dob.year}-${padNumber(dob.month)}-${padNumber(dob.day)}`;
  return { startDate: date, endDate: date };
};

const defaultErrorMessage = "Failed to update personal information";

export default function PersonalEditForm({
  personId,
  informalName = "",
  preferredLastName = "",
  dob,
  gender = "",
  ssn = "",
  spouseName = "",
  onCancel,
  onSuccess,
  onError
}: PersonalEditModalProps) {
  const { hasPermission } = useCapabilities();
  const canEditSSN = hasPermission(PERMISSION.editSSN);
  const canEditDOB = hasPermission(PERMISSION.editDOB);
  const initialSsnDigits = digitsOnly(ssn) || null;
  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm({
    defaultValues: {
      informalName,
      preferredLastName,
      gender,
      spouseName: spouseName || "",
      // only set the dateOfBirth if the user has permission to edit it
      ...(canEditDOB && { dateOfBirth: formatForDateSelect(dob) }),
      // only set the ssn if the user has permission to edit it
      ...(canEditSSN && { ssn: formatSSN(ssn) }) // Format the SSN for initial display
    },
    mode: "onChange"
  });

  const [updatePerson, { loading }] = useMutation(UPDATE_PERSON_MUTATION);

  async function onSubmit(formValues: FieldValues) {
    const { informalName, preferredLastName, dateOfBirth, gender, ssn, spouseName } = formValues;
    try {
      const nextSsn = !ssn ? null : digitsOnly(ssn);
      const initInput = {
        id: personId,
        preferredFirstName: informalName,
        preferredLastName: preferredLastName || null,
        gender,
        spouseName: spouseName || null,
        ...(canEditDOB && { dateOfBirth: typeof dateOfBirth === "object" ? dateOfBirth?.startDate : null }),
        // Only send SSN when it actually changed so legacy/invalid stored values
        // do not block unrelated personal-field updates.
        ...(canEditSSN && nextSsn !== initialSsnDigits && { ssn: nextSsn })
      };

      const input = toInput(initInput);

      const { data } = await updatePerson({
        variables: { input }
      });
      const {
        updatePerson: { success, person, message }
      } = data;

      if (success) {
        return onSuccess(person);
      }

      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="personal-edit-form">
      {canEditDOB && (
        <Controller
          control={control}
          name="dateOfBirth"
          render={({ field: { value, onChange, ref, ...rest } }) => (
            <DateSelect
              {...rest}
              startFrom={new Date()}
              value={value ?? ""}
              onChange={onChange}
              label="Date of Birth"
              placeholder="MM/DD/YYYY"
              supportingText="MM/DD/YYYY"
              error={!!errors.dateOfBirth}
              errorMessage={errors.dateOfBirth?.message}
              useSingleDate={true}
              displayFormat="MM/DD/YYYY"
              maxDate={new Date()}
            />
          )}
        />
      )}

      <Controller
        control={control}
        name="gender"
        render={({ field: { onChange, value } }) => (
          <Select
            value={value ?? ""}
            onSelect={onChange}
            label="Gender Identity"
            options={genderOptions}
            error={!!errors.gender}
            errorText={errors.gender?.message}
          />
        )}
      />

      {canEditSSN && (
        <Controller
          control={control}
          name="ssn"
          rules={{
            validate: (value) => {
              const digits = value ? digitsOnly(value) : "";
              // Unchanged (including legacy invalid) values are allowed; we omit them on submit.
              if (digits === (initialSsnDigits ?? "")) return true;
              if (!digits) return true;
              if (!/^\d{3}-\d{2}-\d{4}$/.test(value ?? "")) {
                return "Invalid SSN format";
              }
              if (!isValidSsn(digits)) {
                return ssnValidationMessage;
              }
              return true;
            }
          }}
          render={({ field }) => {
            const handleSsnChange = (e: Event) => {
              field.onChange(formatSSN((e.target as HTMLInputElement).value));
            };

            return (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={handleSsnChange}
                label="Social Security Number"
                placeholder="###-##-####"
                supportingText="###-##-####"
                error={!!errors.ssn}
                errorText={errors.ssn?.message}
              />
            );
          }}
        />
      )}

      <Controller
        control={control}
        name="informalName"
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Preferred First Name"
            error={!!errors.informalName}
            errorText={errors.informalName?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="preferredLastName"
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Preferred Last Name"
            error={!!errors.preferredLastName}
            errorText={errors.preferredLastName?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="spouseName"
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Spouse Full Name"
            error={!!errors.spouseName}
            errorText={errors.spouseName?.message}
          />
        )}
      />

      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loading || !isValid} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
